import React, { FC, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Tabs, List } from 'antd';
import { connect } from 'react-redux';
import { RootState } from '../../../../rootReducer';
import { AppDispatch } from '../../../../store';
import { getImageAnnotations, imageSelected, setIsPhotocardSelected, onNodeChange, setStatusTable } from '../annotation.slice';
import StitchedView from '../../StitchedView/StitchedView';
import { imageUnloaded, setSaveFinding, generatePickUnselected } from '../../Canvas/canvas.slice';
import UseImagesHelpers from './Hooks/UseImagesHelpers';
import ImageListItem from './ImageListItem';
import { sortImagesAscByTopFunction, TABLE_STATUS } from '../../../../shared/Helpers';
import { ParserContext } from '../../contexts/ParserContext';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { isStringNullOrEmpty } from '../AnnotationForm/Utils';
import { AnnotationManagerPrefix } from '../Helpers';

const { TabPane } = Tabs;

type Props = ReturnType<typeof mapStateToProps> & ReturnType<typeof mapDispatchToProps>;

const TabsConstants = {
  First: '1',
  Second: '2'
};

const Images: FC<RouteComponentProps<any> & Props> = ({
  history,
  match,
  rawImages,
  selectedSequenceInfo,
  selectedName,
  imageLoading,
  showCreateAnnotationModal,
  validationModalData,
  standardLoaded,
  presentAnnotations,
  saveFinding,
  isPhotocardSelected,
  tableStatus,
  pathContext,
  onSelectImage,
  onGetImageAnnotations,
  onSaveFinding,
  onSetIsPhotocardSelected,
  onNodeChanged,
  onImageUnloaded,
  onSetStatusTable,
  onClearToolbar
}) => {
  const { parser } = useContext(ParserContext);

  const images = useMemo(() => selectedSequenceInfo && selectedSequenceInfo.images.filter(x => !x.isHidden), [selectedSequenceInfo]);
  const hasStitchedView = useMemo(() => selectedSequenceInfo && selectedSequenceInfo.hasStitchedView, [selectedSequenceInfo]);
  const sequencePath = useMemo(() => parser.parsedSequencePath(match.params), [match.params, parser]);

  const [accessKey, setAccessKey] = useState(TabsConstants.First);

  const navigateToImage = useCallback(
    (name: string) => {
      history.push(`/${AnnotationManagerPrefix}/${parser.name}/${parser.parsedSequencePath(match.params)}/${name}`);

      if (tableStatus === TABLE_STATUS.EXPANDED) {
        onSetStatusTable(TABLE_STATUS.NORMAL);
      }
    },
    [history, match.params, onSetStatusTable, parser, tableStatus]
  );

  const { selectImage, onImageObjectSelected, onListImageSelected } = UseImagesHelpers(
    sequencePath,
    selectedName,
    imageLoading,
    images,
    showCreateAnnotationModal,
    validationModalData,
    presentAnnotations,
    saveFinding,
    pathContext,
    onSelectImage,
    onGetImageAnnotations,
    onSaveFinding,
    onSetIsPhotocardSelected,
    navigateToImage,
    onClearToolbar
  );

  useEffect(() => {
    const urlImageName = parser.getImageName(match.params);

    if (isStringNullOrEmpty(urlImageName)) {
      onNodeChanged();
      onImageUnloaded();
      return;
    }

    if (urlImageName === selectedName || isStringNullOrEmpty(sequencePath) || !standardLoaded || !selectedSequenceInfo) {
      return;
    }

    selectImage(urlImageName!, isPhotocardSelected);
  }, [
    isPhotocardSelected,
    match.params,
    onImageUnloaded,
    onNodeChanged,
    parser,
    selectImage,
    selectedName,
    selectedSequenceInfo,
    sequencePath,
    standardLoaded
  ]);

  if (!images) {
    return (
      <Tabs className={'images-tab'} defaultActiveKey={TabsConstants.First}>
        <TabPane tab='Nothing is selected' key={TabsConstants.First}></TabPane>
      </Tabs>
    );
  }

  const sortedImages = images.sort(sortImagesAscByTopFunction);

  return (
    <Tabs className={'images-tab'} defaultActiveKey={TabsConstants.First} activeKey={accessKey} onChange={key => setAccessKey(key)}>
      <TabPane tab='List' key={TabsConstants.First} style={{ height: 'calc(100vh - 177px)', overflow: 'auto' }}>
        <List
          bordered
          style={{ background: 'white', textAlign: 'center', fontSize: 'bold' }}
          dataSource={sortedImages}
          renderItem={item => <ImageListItem {...{ item, selectedName, onListImageSelected, isPhotocardSelected, onSetIsPhotocardSelected }} />}
        />
      </TabPane>
      {hasStitchedView && (
        <TabPane tab='Images' key={TabsConstants.Second} forceRender={false} style={{ height: 'calc(100vh - 177px)' }}>
          {accessKey === TabsConstants.Second && (
            <StitchedView
              id='canvas'
              rawImages={rawImages}
              isStitched={true}
              resetCanvas={false}
              onImageObjectSelected={onImageObjectSelected}
            ></StitchedView>
          )}
        </TabPane>
      )}
    </Tabs>
  );
};

const mapStateToProps = (state: RootState) => ({
  rawImages: state.annotation.images,
  selectedSequenceInfo: state.annotation.selectedSequenceInfo,
  selectedName: state.annotation.selectedName,
  imageLoading: state.canvas.loading,
  showCreateAnnotationModal: state.annotationForm.showCreateAnnotationModal,
  validationModalData: state.annotationValidationModal.validationModalData,
  standardLoaded: !!state.annotation.annotationStandardDetails,
  presentAnnotations: state.annotation.presentAnnotations,
  saveFinding: state.canvas.saveFinding,
  isPhotocardSelected: state.annotation.isPhotocardSelected,
  tableStatus: state.annotation.tableStatus,
  pathContext: state.annotation.pathContext
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  onSelectImage: (url: string, name: string) => dispatch(imageSelected(url, name)),
  onGetImageAnnotations: (isPhotocard: boolean) => dispatch(getImageAnnotations(isPhotocard)),
  onSaveFinding: (saveFinding: boolean) => dispatch(setSaveFinding(saveFinding)),
  onSetIsPhotocardSelected: (isSelected: boolean) => dispatch(setIsPhotocardSelected(isSelected)),
  onNodeChanged: () => dispatch(onNodeChange()),
  onImageUnloaded: () => dispatch(imageUnloaded()),
  onSetStatusTable: (expandHide: number) => dispatch(setStatusTable(expandHide)),
  onClearToolbar: () => dispatch(generatePickUnselected(true))
});

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(Images));
